/**
 * Reusable Supabase utilities for Clientelo
 * Eliminates duplicate fetch patterns across stores
 */

import { supabase } from '../lib/supabase';
import { logger } from './logger';

export interface FetchOptions {
  orderBy?: string;
  ascending?: boolean;
  filters?: Record<string, any>;
  limit?: number;
  timeout?: number;
}

/**
 * Generic fetch function for empresa-scoped data
 * Replaces duplicate fetch patterns in all stores
 * 
 * @example
 * const { data, error } = await fetchByEmpresaId<Producto>('productos', empresaId);
 */
export async function fetchByEmpresaId<T>(
  tableName: string,
  empresaId: string,
  options: FetchOptions = {}
): Promise<{ data: T[] | null; error: any }> {
  // Validate empresa ID
  if (!empresaId) {
    logger.warn(`❌ No empresa ID provided for ${tableName}`);
    return { data: [], error: null };
  }

  logger.debug(`🔍 Fetching ${tableName} for empresa:`, empresaId);

  // Create timeout promise
  const timeoutMs = options.timeout || 15000; // 15 second default
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error(`Request timeout after ${timeoutMs}ms`)), timeoutMs);
  });

  try {
    // Build query
    let query = supabase
      .from(tableName)
      .select('*')
      .eq('empresa_id', empresaId);

    // Apply additional filters
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          query = query.eq(key, value);
        }
      });
    }

    // Apply ordering
    query = query.order(
      options.orderBy || 'created_at',
      { ascending: options.ascending ?? false }
    );

    // Apply limit if specified
    if (options.limit) {
      query = query.limit(options.limit);
    }

    // Race between query and timeout
    const { data, error } = await Promise.race([
      query,
      timeoutPromise
    ]) as any;

    if (error) {
      logger.error(`❌ Error fetching ${tableName}:`, {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      return { data: null, error };
    }

    logger.debug(`✅ ${tableName} loaded:`, data?.length || 0, 'items');
    return { data: data || [], error: null };

  } catch (error) {
    logger.error(`❌ Exception fetching ${tableName}:`, error);
    return { 
      data: null, 
      error: error instanceof Error ? error : new Error('Unknown error')
    };
  }
}

/**
 * Generic create function for empresa-scoped data
 * 
 * @example
 * const { data, error } = await createForEmpresa<Producto>('productos', empresaId, newProduct);
 */
export async function createForEmpresa<T>(
  tableName: string,
  empresaId: string,
  data: Partial<T>
): Promise<{ data: T | null; error: any }> {
  if (!empresaId) {
    const error = new Error(`No empresa ID provided for ${tableName} creation`);
    logger.error(error.message);
    return { data: null, error };
  }

  logger.debug(`➕ Creating ${tableName} for empresa:`, empresaId);

  try {
    const payload = {
      ...data,
      empresa_id: empresaId,
    };

    const { data: created, error } = await supabase
      .from(tableName)
      .insert([payload])
      .select()
      .single();

    if (error) {
      logger.error(`❌ Error creating ${tableName}:`, error);
      return { data: null, error };
    }

    logger.debug(`✅ ${tableName} created successfully`);
    return { data: created as T, error: null };

  } catch (error) {
    logger.error(`❌ Exception creating ${tableName}:`, error);
    return { 
      data: null, 
      error: error instanceof Error ? error : new Error('Unknown error')
    };
  }
}

/**
 * Generic update function
 * 
 * @example
 * const { data, error } = await updateById<Producto>('productos', productId, updates);
 */
export async function updateById<T>(
  tableName: string,
  id: string,
  updates: Partial<T>
): Promise<{ data: T | null; error: any }> {
  if (!id) {
    const error = new Error(`No ID provided for ${tableName} update`);
    logger.error(error.message);
    return { data: null, error };
  }

  logger.debug(`📝 Updating ${tableName}:`, id);

  try {
    const { data, error } = await supabase
      .from(tableName)
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      logger.error(`❌ Error updating ${tableName}:`, error);
      return { data: null, error };
    }

    logger.debug(`✅ ${tableName} updated successfully`);
    return { data: data as T, error: null };

  } catch (error) {
    logger.error(`❌ Exception updating ${tableName}:`, error);
    return { 
      data: null, 
      error: error instanceof Error ? error : new Error('Unknown error')
    };
  }
}

/**
 * Generic delete function
 * 
 * @example
 * const { error } = await deleteById('productos', productId);
 */
export async function deleteById(
  tableName: string,
  id: string
): Promise<{ error: any }> {
  if (!id) {
    const error = new Error(`No ID provided for ${tableName} deletion`);
    logger.error(error.message);
    return { error };
  }

  logger.debug(`🗑️ Deleting ${tableName}:`, id);

  try {
    const { error } = await supabase
      .from(tableName)
      .delete()
      .eq('id', id);

    if (error) {
      logger.error(`❌ Error deleting ${tableName}:`, error);
      return { error };
    }

    logger.debug(`✅ ${tableName} deleted successfully`);
    return { error: null };

  } catch (error) {
    logger.error(`❌ Exception deleting ${tableName}:`, error);
    return { 
      error: error instanceof Error ? error : new Error('Unknown error')
    };
  }
}

/**
 * Generates a unique sale/invoice number with retry logic
 * Now with max attempts to prevent infinite loops
 * 
 * @example
 * const numero = await generateUniqueNumero('ventas', empresaId, 'V-001');
 */
export async function generateUniqueNumero(
  tableName: string,
  empresaId: string,
  preferredNumero?: string,
  maxAttempts: number = 100
): Promise<string> {
  const trimmedNumero = preferredNumero?.trim();
  const baseNumero = trimmedNumero && trimmedNumero.length > 0
    ? trimmedNumero
    : `${tableName.charAt(0).toUpperCase()}-${Date.now().toString().slice(-6)}`;

  let numeroFinal = baseNumero;
  let attempt = 0;

  while (attempt < maxAttempts) {
    const { data, error } = await supabase
      .from(tableName)
      .select('id')
      .eq('empresa_id', empresaId)
      .eq('numero', numeroFinal)
      .maybeSingle();

    // No rows found - number is unique
    if (error && error.code !== 'PGRST116') {
      logger.error(`❌ Error checking unique numero:`, error);
      throw error;
    }

    if (!data) {
      logger.debug(`✅ Generated unique numero: ${numeroFinal}`);
      return numeroFinal;
    }

    // Number exists, try again with suffix
    attempt += 1;
    const suffix = String(attempt).padStart(2, '0');
    numeroFinal = `${baseNumero}-R${suffix}`;
  }

  // Max attempts reached
  throw new Error(
    `Could not generate unique ${tableName} number after ${maxAttempts} attempts`
  );
}



